/**
 * Shared fast-check arbitraries for Link domain types.
 *
 * Reused by `packages/crypto`, `apps/api`, and `apps/web` PBT suites.
 *
 * NOTE: The fixture interfaces in this file mirror the shape of rows
 * declared by `../db/schema` (task 2.1). Once the schema's
 * `InferSelectModel` types stabilize, the local fixture interfaces below
 * should be replaced with the schema-derived types — the field names are
 * already aligned so the swap is mechanical.
 */

import fc from "fast-check";

import {
  ABUSE_REASONS,
  ACCOUNT_STATUSES,
  CHANNEL_TYPES,
  DM_MAX_PARTICIPANTS,
  ENVELOPE_KINDS,
  FILE_MAX_BYTES,
  FRIEND_STATUSES,
  MESSAGE_STORAGE_KINDS,
  MNEMONIC_WORD_COUNT,
  PLATFORM_KINDS,
  ROLE_PERMISSIONS,
  SNIPPET_MAX_BYTES,
  SPACE_TYPES,
  TEXT_MESSAGE_MAX_CHARS,
  type AbuseReason,
  type AccountStatus,
  type ChannelType,
  type EnvelopeKind,
  type FriendStatus,
  type MessageStorageKind,
  type PlatformKind,
  type RolePermissionFlag,
  type SpaceType,
} from "../api/constants";

// Re-export the bits callers most commonly need with the arbitraries.
export {
  ABUSE_REASONS,
  ACCOUNT_STATUSES,
  CHANNEL_TYPES,
  DM_MAX_PARTICIPANTS,
  ENVELOPE_KINDS,
  FILE_MAX_BYTES,
  FRIEND_STATUSES,
  MESSAGE_STORAGE_KINDS,
  MNEMONIC_WORD_COUNT,
  PLATFORM_KINDS,
  ROLE_PERMISSIONS,
  SNIPPET_MAX_BYTES,
  SPACE_TYPES,
  TEXT_MESSAGE_MAX_CHARS,
};

const ROLE_PERMISSION_FLAGS = Object.keys(
  ROLE_PERMISSIONS,
) as RolePermissionFlag[];

// ---------------------------------------------------------------------------
// Fixture interfaces (will be swapped for schema-derived `InferSelectModel`
// types from `../db/schema` in a follow-up cleanup).
// ---------------------------------------------------------------------------

export interface AccountFixture {
  xid: string;
  accountIdHash: Uint8Array; // 32 bytes — SHA-256(publicKeyRaw)
  publicKeyRaw: Uint8Array; // 65 bytes — uncompressed SEC1
  displayName: string | null;
  status: AccountStatus;
  createdAt: Date;
}

export interface SpaceFixture {
  xid: string;
  spaceType: SpaceType;
  ownerAccountXid: string;
  name: string;
  iconUrl: string | null;
  createdAt: Date;
  deletedAt: Date | null;
}

export interface ChannelFixture {
  xid: string;
  spaceXid: string;
  channelType: ChannelType;
  name: string;
  topic: string | null;
  sortPosition: number;
  archivedAt: Date | null;
  createdAt: Date;
}

export interface MessageFixture {
  xid: string;
  channelXid: string;
  authorAccountXid: string | null;
  storageKind: MessageStorageKind;
  envelopeKind: EnvelopeKind | null; // only meaningful for E2E messages
  parentMessageXid: string | null;
  createdAt: Date;
  editedAt: Date | null;
  deletedAt: Date | null;
  /** Only meaningful in a Secrets_Channel. */
  expiresAt: Date | null;
}

export interface FileMetadataFixture {
  xid: string;
  messageXid: string | null;
  uploaderAccountXid: string;
  storageKind: MessageStorageKind;
  filename: string;
  folderPath: string | null;
  mimeType: string;
  byteSize: number;
  sha256: Uint8Array; // 32 bytes
  finalizedAt: Date | null;
}

export interface RoleFixture {
  xid: string;
  spaceXid: string;
  name: string;
  color: string; // CSS hex, e.g. "#7c3aed"
  sortPosition: number;
  permissions: number; // bitmask of ROLE_PERMISSIONS
}

export interface FriendEdgeFixture {
  accountIdHashA: Uint8Array;
  accountIdHashB: Uint8Array;
  status: FriendStatus;
  createdAt: Date;
  updatedAt: Date;
}

export interface SecretsEnvelopeContext {
  channelId: string;
  messageId: string;
}

export interface KeypairFixture {
  privateKeyJwk: JsonWebKey;
  publicKeyJwk: JsonWebKey;
  publicKeyRaw: Uint8Array; // 65 bytes uncompressed SEC1
  accountIdHash: Uint8Array; // 32 bytes SHA-256(publicKeyRaw)
}

// ---------------------------------------------------------------------------
// XID arbitrary
// ---------------------------------------------------------------------------

// XID = 12 bytes encoded as base32-hex (lowercase 0-9 a-v) → 20 chars.
const XID_ALPHABET = "0123456789abcdefghijklmnopqrstuv";

/** Arbitrary producing an xid-shaped 20-character lowercase base32 string. */
export function xidArb(): fc.Arbitrary<string> {
  return fc
    .array(fc.integer({ min: 0, max: 31 }), {
      minLength: 20,
      maxLength: 20,
    })
    .map((indices) => indices.map((i) => XID_ALPHABET[i]!).join(""));
}

// ---------------------------------------------------------------------------
// BIP-39 mnemonic arbitrary (curated valid English 12-word phrases)
// ---------------------------------------------------------------------------

/**
 * Curated list of valid 12-word BIP-39 English mnemonics drawn from the
 * publicly published Trezor BIP-39 test vectors
 * (https://github.com/trezor/python-mnemonic/blob/master/vectors.json).
 * Each phrase has a valid checksum. Extend this list as needed; do NOT
 * add entries you have not verified against the BIP-39 checksum.
 */
export const VALID_MNEMONICS_12: readonly string[] = [
  "abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about",
  "legal winner thank year wave sausage worth useful legal winner thank yellow",
  "letter advice cage absurd amount doctor acoustic avoid letter advice cage above",
  "zoo zoo zoo zoo zoo zoo zoo zoo zoo zoo zoo wrong",
  "ozone drill grab fiber curtain grace pudding thank cruise elder eight picnic",
  "scheme spot photo card baby mountain device kick cradle pact join borrow",
  "cat swing flag economy stadium alone churn speed unique patch report train",
  "vessel ladder alter error federal sibling chat ability sun glass valve picture",
] as const;

/** Arbitrary producing a known-valid 12-word BIP-39 English mnemonic. */
export function validMnemonicArb(): fc.Arbitrary<string> {
  // Sanity check: keep all curated phrases at the agreed word count.
  for (const m of VALID_MNEMONICS_12) {
    if (m.split(" ").length !== MNEMONIC_WORD_COUNT) {
      throw new Error(
        `VALID_MNEMONICS_12 contains an entry with ${m.split(" ").length} words; ` +
          `expected ${MNEMONIC_WORD_COUNT}.`,
      );
    }
  }
  return fc.constantFrom(...VALID_MNEMONICS_12);
}

// ---------------------------------------------------------------------------
// P-256 keypair arbitrary (lazy pool, generated via Web Crypto and cached)
// ---------------------------------------------------------------------------

let keypairPool: KeypairFixture[] | null = null;

async function generateKeypairFixture(): Promise<KeypairFixture> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error(
      "Web Crypto subtle is not available in this runtime. " +
        "Keypair arbitraries require a Web Crypto-capable environment " +
        "(modern browsers, Node 20+, or Bun).",
    );
  }
  const kp = (await subtle.generateKey(
    { name: "ECDSA", namedCurve: "P-256" },
    true,
    ["sign", "verify"],
  )) as CryptoKeyPair;

  const privateKeyJwk = await subtle.exportKey("jwk", kp.privateKey);
  const publicKeyJwk = await subtle.exportKey("jwk", kp.publicKey);
  const publicKeyRaw = new Uint8Array(
    await subtle.exportKey("raw", kp.publicKey),
  );
  const accountIdHash = new Uint8Array(
    await subtle.digest("SHA-256", publicKeyRaw),
  );

  return { privateKeyJwk, publicKeyJwk, publicKeyRaw, accountIdHash };
}

/**
 * Pre-fill the P-256 keypair pool. Property tests run thousands of
 * iterations, so generating keys on every shrink is too slow — call this
 * once in your test setup (e.g. `beforeAll`) and then `keypairArb()` will
 * sample from the pool synchronously.
 *
 * Default size is 12 — large enough to give shrinkers diversity, small
 * enough to keep setup under ~150ms on a modern machine.
 */
export async function prepareKeypairPool(size = 12): Promise<void> {
  if (keypairPool && keypairPool.length >= size) return;
  const pool: KeypairFixture[] = [];
  for (let i = 0; i < size; i++) {
    pool.push(await generateKeypairFixture());
  }
  keypairPool = pool;
}

/** Reset the keypair pool. Useful between test suites. */
export function resetKeypairPool(): void {
  keypairPool = null;
}

/**
 * Arbitrary producing a P-256 keypair fixture from the pre-loaded pool.
 * Throws if the pool has not been initialized.
 */
export function keypairArb(): fc.Arbitrary<KeypairFixture> {
  if (!keypairPool || keypairPool.length === 0) {
    throw new Error(
      "Keypair pool not prepared. Call `await prepareKeypairPool()` " +
        "in your test setup before using `keypairArb()`.",
    );
  }
  return fc.constantFrom(...keypairPool);
}

// ---------------------------------------------------------------------------
// Account ID hash arbitrary
// ---------------------------------------------------------------------------

/** Arbitrary producing a 32-byte SHA-256-shaped account_id_hash. */
export function accountIdHashArb(): fc.Arbitrary<Uint8Array> {
  return fc
    .uint8Array({ minLength: 32, maxLength: 32 })
    .map((arr) => new Uint8Array(arr));
}

// ---------------------------------------------------------------------------
// Account arbitrary (does not require the keypair pool)
// ---------------------------------------------------------------------------

/**
 * Arbitrary producing a 65-byte uncompressed-SEC1-shaped public key.
 *
 * NOTE: This produces a byte buffer of the right shape with a `0x04` prefix
 * but the trailing bytes are random and so the point may not lie on P-256.
 * For tests that need a *valid* curve point use `keypairArb()` instead.
 */
export function publicKeyRawShapeArb(): fc.Arbitrary<Uint8Array> {
  return fc
    .uint8Array({ minLength: 64, maxLength: 64 })
    .map((tail) => {
      const out = new Uint8Array(65);
      out[0] = 0x04;
      out.set(tail, 1);
      return out;
    });
}

const TIMESTAMP_RANGE = {
  min: new Date("2024-01-01T00:00:00Z"),
  max: new Date("2030-01-01T00:00:00Z"),
};
const dateArb = () => fc.date(TIMESTAMP_RANGE);
const optionalDateArb = () => fc.option(dateArb(), { nil: null });

export function accountArb(): fc.Arbitrary<AccountFixture> {
  return fc.record({
    xid: xidArb(),
    accountIdHash: accountIdHashArb(),
    publicKeyRaw: publicKeyRawShapeArb(),
    displayName: fc.option(
      fc.string({ minLength: 1, maxLength: 32 }),
      { nil: null },
    ),
    status: fc.constantFrom(...ACCOUNT_STATUSES),
    createdAt: dateArb(),
  });
}

// ---------------------------------------------------------------------------
// Space arbitrary
// ---------------------------------------------------------------------------

export function spaceArb(opts?: {
  spaceType?: SpaceType;
}): fc.Arbitrary<SpaceFixture> {
  const spaceTypeArb = opts?.spaceType
    ? fc.constant(opts.spaceType)
    : fc.constantFrom(...SPACE_TYPES);
  return fc.record({
    xid: xidArb(),
    spaceType: spaceTypeArb,
    ownerAccountXid: xidArb(),
    name: fc.string({ minLength: 1, maxLength: 64 }),
    iconUrl: fc.option(fc.webUrl(), { nil: null }),
    createdAt: dateArb(),
    deletedAt: optionalDateArb(),
  });
}

// ---------------------------------------------------------------------------
// Channel arbitrary
// ---------------------------------------------------------------------------

export function channelArb(opts?: {
  spaceXid?: string;
  channelType?: ChannelType;
}): fc.Arbitrary<ChannelFixture> {
  const channelTypeArb = opts?.channelType
    ? fc.constant(opts.channelType)
    : fc.constantFrom(...CHANNEL_TYPES);
  const spaceXidArb = opts?.spaceXid ? fc.constant(opts.spaceXid) : xidArb();
  return fc.record({
    xid: xidArb(),
    spaceXid: spaceXidArb,
    channelType: channelTypeArb,
    name: fc.string({ minLength: 1, maxLength: 64 }),
    topic: fc.option(fc.string({ maxLength: 256 }), { nil: null }),
    sortPosition: fc.integer({ min: 0, max: 1024 }),
    archivedAt: optionalDateArb(),
    createdAt: dateArb(),
  });
}

// ---------------------------------------------------------------------------
// Message arbitrary
// ---------------------------------------------------------------------------

export function messageArb(opts?: {
  channelXid?: string;
  storageKind?: MessageStorageKind;
  withParent?: boolean;
  withExpiry?: boolean;
}): fc.Arbitrary<MessageFixture> {
  const channelXidArb = opts?.channelXid
    ? fc.constant(opts.channelXid)
    : xidArb();
  const storageKindArb = opts?.storageKind
    ? fc.constant(opts.storageKind)
    : fc.constantFrom(...MESSAGE_STORAGE_KINDS);
  const parentArb =
    opts?.withParent === true
      ? xidArb()
      : opts?.withParent === false
        ? fc.constant(null)
        : fc.option(xidArb(), { nil: null });
  const expiresArb =
    opts?.withExpiry === true
      ? dateArb()
      : opts?.withExpiry === false
        ? fc.constant(null)
        : optionalDateArb();
  return fc.record({
    xid: xidArb(),
    channelXid: channelXidArb,
    authorAccountXid: fc.option(xidArb(), { nil: null }),
    storageKind: storageKindArb,
    envelopeKind: fc.option(fc.constantFrom(...ENVELOPE_KINDS), { nil: null }),
    parentMessageXid: parentArb,
    createdAt: dateArb(),
    editedAt: optionalDateArb(),
    deletedAt: optionalDateArb(),
    expiresAt: expiresArb,
  });
}

/** Arbitrary producing UTF-8 text bounded by `TEXT_MESSAGE_MAX_CHARS`. */
export function textMessageBodyArb(): fc.Arbitrary<string> {
  return fc.string({ minLength: 1, maxLength: TEXT_MESSAGE_MAX_CHARS });
}

/** Arbitrary producing a snippet body bounded by `SNIPPET_MAX_BYTES`. */
export function snippetBodyArb(): fc.Arbitrary<string> {
  // 1 char ≈ 1 byte for ASCII; cap conservatively to stay under the byte limit.
  return fc.string({ minLength: 1, maxLength: SNIPPET_MAX_BYTES });
}

// ---------------------------------------------------------------------------
// File metadata arbitrary
// ---------------------------------------------------------------------------

const COMMON_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/svg+xml",
  "application/pdf",
  "text/plain",
  "text/markdown",
  "application/octet-stream",
] as const;

export function fileMetadataArb(opts?: {
  storageKind?: MessageStorageKind;
}): fc.Arbitrary<FileMetadataFixture> {
  const storageKindArb = opts?.storageKind
    ? fc.constant(opts.storageKind)
    : fc.constantFrom(...MESSAGE_STORAGE_KINDS);
  return fc.record({
    xid: xidArb(),
    messageXid: fc.option(xidArb(), { nil: null }),
    uploaderAccountXid: xidArb(),
    storageKind: storageKindArb,
    filename: fc
      .string({ minLength: 1, maxLength: 64 })
      .map((s) => s.replace(/[\u0000\/\\]/g, "_")),
    folderPath: fc.option(
      fc
        .array(fc.string({ minLength: 1, maxLength: 16 }), {
          minLength: 1,
          maxLength: 4,
        })
        .map((parts) =>
          parts.map((p) => p.replace(/[\u0000\/\\]/g, "_")).join("/"),
        ),
      { nil: null },
    ),
    mimeType: fc.constantFrom(...COMMON_MIME_TYPES),
    byteSize: fc.integer({ min: 1, max: FILE_MAX_BYTES }),
    sha256: fc
      .uint8Array({ minLength: 32, maxLength: 32 })
      .map((arr) => new Uint8Array(arr)),
    finalizedAt: optionalDateArb(),
  });
}

// ---------------------------------------------------------------------------
// Role permissions arbitrary
// ---------------------------------------------------------------------------

const PERMISSION_BITS_TOTAL = ROLE_PERMISSION_FLAGS.reduce(
  (mask, flag) => mask | ROLE_PERMISSIONS[flag],
  0,
);

/**
 * Arbitrary producing a role permission bitmask — any subset of the 8
 * defined permission flags. Range: [0, 255].
 */
export function permissionsBitmaskArb(): fc.Arbitrary<number> {
  return fc.integer({ min: 0, max: PERMISSION_BITS_TOTAL });
}

/** Arbitrary producing the *set* of permission flag names equivalent to a bitmask. */
export function permissionsSetArb(): fc.Arbitrary<Set<RolePermissionFlag>> {
  return permissionsBitmaskArb().map(
    (mask) =>
      new Set(
        ROLE_PERMISSION_FLAGS.filter(
          (flag) => (mask & ROLE_PERMISSIONS[flag]) !== 0,
        ),
      ),
  );
}

export function roleArb(opts?: {
  spaceXid?: string;
}): fc.Arbitrary<RoleFixture> {
  const spaceXidArb = opts?.spaceXid ? fc.constant(opts.spaceXid) : xidArb();
  return fc.record({
    xid: xidArb(),
    spaceXid: spaceXidArb,
    name: fc.string({ minLength: 1, maxLength: 32 }),
    color: fc
      .integer({ min: 0, max: 0xffffff })
      .map((n) => `#${n.toString(16).padStart(6, "0")}`),
    sortPosition: fc.integer({ min: 0, max: 1024 }),
    permissions: permissionsBitmaskArb(),
  });
}

// ---------------------------------------------------------------------------
// Friend state arbitrary
// ---------------------------------------------------------------------------

export function friendEdgeArb(): fc.Arbitrary<FriendEdgeFixture> {
  return fc
    .tuple(accountIdHashArb(), accountIdHashArb())
    .filter(([a, b]) => !uint8ArrayEquals(a, b))
    .chain(([a, b]) =>
      fc.record({
        accountIdHashA: fc.constant(a),
        accountIdHashB: fc.constant(b),
        status: fc.constantFrom(...FRIEND_STATUSES),
        createdAt: dateArb(),
        updatedAt: dateArb(),
      }),
    );
}

function uint8ArrayEquals(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Secrets envelope context arbitrary
// ---------------------------------------------------------------------------

/**
 * Arbitrary producing the `{ channelId, messageId }` context tuple that
 * `wrapSecrets` / `unwrapSecrets` (and `encryptE2e` / `decryptE2e`) use as
 * AAD-binding input. Both ids are xid-shaped strings.
 */
export function secretsEnvelopeContextArb(): fc.Arbitrary<SecretsEnvelopeContext> {
  return fc.record({
    channelId: xidArb(),
    messageId: xidArb(),
  });
}

// ---------------------------------------------------------------------------
// DM-participant-count arbitrary (handy for dm_conversations PBT)
// ---------------------------------------------------------------------------

/**
 * Arbitrary producing a valid DM participant count: a 1:1 has 2
 * participants and a group DM has 3..`DM_MAX_PARTICIPANTS`.
 */
export function dmParticipantCountArb(): fc.Arbitrary<number> {
  return fc.integer({ min: 2, max: DM_MAX_PARTICIPANTS });
}

// ---------------------------------------------------------------------------
// Platform-kind / abuse-reason convenience arbitraries
// ---------------------------------------------------------------------------

export function platformKindArb(): fc.Arbitrary<PlatformKind> {
  return fc.constantFrom(...PLATFORM_KINDS);
}

export function abuseReasonArb(): fc.Arbitrary<AbuseReason> {
  return fc.constantFrom(...ABUSE_REASONS);
}
